import React, { useState } from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  CircularProgress,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import ConfirmModal from "components/react-lib/generic-ui/ConfirmModal";
import { deleteDoc, doc, updateDoc } from "firebase/firestore";
import { db, functions } from "firebaseApp";
import { saveAs } from "file-saver";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import DescriptionIcon from "@mui/icons-material/Description";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { fonts } from "fonts";
import { getThemeColor } from "colors";
import HttpIcon from "@mui/icons-material/Http";
import { useTheme } from "@emotion/react";
import { httpsCallable } from "firebase/functions";
import PdfOcrIcon from "components/custom-icons/PdfOcrIcon";
import { useDispatch } from "react-redux";
import { popupActions } from "store/react-lib/generic-ui/popup";
import { useNavigate } from "react-router-dom";
import { editorActions } from "store/editor";
import DeleteIcon from "@mui/icons-material/Delete";
import FolderZipIcon from "@mui/icons-material/FolderZip";

export default function FileMenu({
  file,
  color,
  isAttachment = false,
  customRemove = null,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [creatingDocument, setCreatingDocument] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [performingOCR, setPerformingOCR] = useState(false);
  const [zipping, setZipping] = useState(false);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadText = () => {
    let url;
    if (
      file.type === "url" ||
      file.type === "text" ||
      file.type === "youtube"
    ) {
      const blob = new Blob([file.content], { type: "text/plain" });
      url = URL.createObjectURL(blob);
    } else {
      url = file.rawTextFileUrl;
    }
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank"; // Set target to _blank to open in a new tab
    link.rel = "noopener noreferrer"; // Recommended for security reasons
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link); // Clean up the DOM
    handleClose();
  };

  const handleDownloadFile = () => {
    const link = document.createElement("a");
    link.href = file.fileUrl || file.imageUrl;
    link.target = "_blank"; // Set target to _blank to open in a new tab
    link.rel = "noopener noreferrer"; // Recommended for security reasons
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link); // Clean up the DOM
    handleClose();
  };

  const handleDelete = async () => {
    try {
      if (customRemove) {
        customRemove();
        return;
      }
      const collection = isAttachment ? "attachments" : "files";
      await deleteDoc(doc(db, collection, file.id));
    } catch (error) {
      console.log(error);
    }
  };

  const handleOpenUrl = () => {
    window.open(file.url, "_blank");
  };

  const handleDeleteClick = () => {
    if (isAttachment) {
      handleClose();
      handleDelete();
    } else {
      setConfirmingDelete(true);
      handleClose();
    }
  };

  const handleCreateDocument = async () => {
    try {
      setCreatingDocument(true);
      const res = await httpsCallable(
        functions,
        "createDocumentFromFile"
      )({
        fileId: file.id,
      });
      const resData = res.data;
      if (!resData.success) {
        dispatch(popupActions.showError(resData.error));
      } else {
        dispatch(editorActions.setActiveDocument(resData.document));
        navigate("/editor");
      }
    } catch (error) {
      console.log(error);
      dispatch(popupActions.showError("Error creating document."));
    } finally {
      setCreatingDocument(false);
    }
  };

  const supportsOCR = () => {
    if (file.type === "image") return true;
    if (file.type === "file" && file.mimeType === "application/pdf")
      return true;
    return false;
  };

  const handleGenerateOCR = async () => {
    try {
      setPerformingOCR(true);
      const res = await httpsCallable(
        functions,
        "performFileOCR"
      )({
        fileId: file.id,
      });
      const resData = res.data;
      if (!resData.success) {
        dispatch(popupActions.showError(resData.error));
      } else {
        dispatch(popupActions.showSuccess("OCR successful!"));
      }
    } catch (error) {
      console.log(error);
      dispatch(popupActions.showError("Couldn't perform OCR."));
    } finally {
      setPerformingOCR(false);
    }
  };

  const renderGenerateOCRMenuItem = () => {
    if (!supportsOCR() || file.ocr) return null;
    return (
      <MenuItem
        onClick={handleGenerateOCR}
        disabled={performingOCR || file.processingOCR}
      >
        <ListItemIcon>
          {performingOCR || file.processingOCR ? (
            <CircularProgress size={24} color="secondary" />
          ) : (
            <PdfOcrIcon color={theme.palette.text.main} />
          )}
        </ListItemIcon>
        <Typography fontFamily={fonts.geist}>Generate OCR</Typography>
      </MenuItem>
    );
  };

  const renderOCRMenuItem = () => {
    if (!file.ocr) return null;
    return (
      <MenuItem onClick={handleCreateDocument} disabled={creatingDocument}>
        <ListItemIcon>
          {creatingDocument ? (
            <CircularProgress size={24} color="secondary" />
          ) : (
            <PdfOcrIcon color={theme.palette.text.main} />
          )}
        </ListItemIcon>
        <Typography fontFamily={fonts.geist}>Document from OCR</Typography>
      </MenuItem>
    );
  };

  const handleZip = async () => {
    try {
      setZipping(true);
      const res = await httpsCallable(
        functions,
        "createOCRZip"
      )({
        fileId: file.id,
      });
      const data = res.data;
      console.log(data);
      if (!data.success) {
        dispatch(popupActions.showError(data.error));
      } else {
        const downloadLink = document.createElement("a");
        downloadLink.href = data.downloadURL;
        downloadLink.download = data.filename;
        downloadLink.target = "_blank";
        // Trigger download
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    } catch (error) {
      console.log(error);
      dispatch(popupActions.showError("Problem processing Zip file."));
    } finally {
      setZipping(false);
    }
  };

  const renderZipMenuItem = () => {
    if (!file.ocr) return null;
    return (
      <MenuItem onClick={handleZip} disabled={zipping}>
        <ListItemIcon>
          {zipping ? (
            <CircularProgress size={24} color="secondary" />
          ) : (
            <FolderZipIcon color={theme.palette.text.main} />
          )}
        </ListItemIcon>
        <Typography fontFamily={fonts.geist}>Download OCR Zip</Typography>
      </MenuItem>
    );
  };

  return (
    <>
      <ConfirmModal
        open={confirmingDelete}
        setOpen={setConfirmingDelete}
        confirmAction={handleDelete}
        title="Delete File"
        message="Are you sure to delete this file?"
      />
      <IconButton
        onClick={handleClick}
        sx={{
          color: color,
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {file.type !== "image" && (
          <MenuItem onClick={handleDownloadText}>
            <ListItemIcon>
              <TextFieldsIcon sx={{ color: theme.palette.text.main }} />
            </ListItemIcon>
            <Typography
              fontFamily={fonts.geist}
              sx={{
                color: theme.palette.text.main,
              }}
            >
              Open raw text
            </Typography>
          </MenuItem>
        )}
        {(file.type === "file" || file.type === "image") && (
          <MenuItem onClick={handleDownloadFile}>
            <ListItemIcon>
              <DescriptionIcon sx={{ color: theme.palette.text.main }} />
            </ListItemIcon>
            <Typography fontFamily={fonts.geist}>Open file</Typography>
          </MenuItem>
        )}
        {file.type === "url" && (
          <MenuItem onClick={handleOpenUrl}>
            <ListItemIcon>
              <HttpIcon sx={{ color: theme.palette.text.main }} />
            </ListItemIcon>
            <Typography fontFamily={fonts.geist}>Open URL</Typography>
          </MenuItem>
        )}
        {renderGenerateOCRMenuItem()}
        {renderOCRMenuItem()}
        {renderZipMenuItem()}
        {isAttachment ? (
          <MenuItem onClick={handleDeleteClick}>
            <ListItemIcon>
              <LinkOffIcon sx={{ color: theme.palette.text.main }} />
            </ListItemIcon>
            <Typography fontFamily={fonts.geist}>Remove</Typography>
          </MenuItem>
        ) : (
          <MenuItem onClick={handleDeleteClick}>
            <ListItemIcon>
              <DeleteIcon sx={{ color: theme.palette.text.main }} />
            </ListItemIcon>
            <Typography fontFamily={fonts.geist}>Delete</Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
